import * as m from '@staiter/i18n';
import { MINUS_SIGN } from '@staiter/i18n/format';
/**
 * S17-FE.1+2+3+4+5+6+7+8 — Финансовый дашборд (executive view).
 *
 * Single-route с FilterBar + KPI grid + 3 inline-SVG charts + top counterparties table.
 * URL state sync через TanStack Router validateSearch (FE.2).
 * Drill-down handlers через useDashboardDrillDown (FE.7).
 * Export-to-Excel + Share-link buttons (FE.8).
 */
import {
  type DashboardCurrency,
  DashboardQuerySchema,
  type DashboardSeriesBucket,
  type DashboardSeriesMetric,
  type FinanceDashboardKpi,
  type MoneyAmount,
  type TopCounterpartiesMetric,
  type TopCounterpartiesResponse,
} from '@staiter/shared';
import { useQuery } from '@tanstack/react-query';
import { Link, createFileRoute, useNavigate } from '@tanstack/react-router';
import {
  ArrowDownRight,
  ArrowUpRight,
  Download,
  Inbox,
  Link as LinkIcon,
  Minus,
  Users,
} from 'lucide-react';
import { useLayoutEffect, useMemo, useRef, useState } from 'react';
import { BentoCard, EmptyState, PageHeader } from '~/components/data';
import { Button, Checkbox, DateRangePicker } from '~/components/primitives';
import { ProvenanceChip } from '~/components/status';
import {
  fetchDashboardKpi,
  fetchDashboardSeries,
  fetchTopCounterparties,
} from '~/lib/api/finance-dashboard';
import { formatMoney } from '~/lib/format';
import { formatMoneyCompact } from '~/lib/format/money';
import styles from './_auth.finance.dashboard.module.css';

// ─── Date helpers ────────────────────────────────────────────────────

function today(): string {
  return new Date().toISOString().slice(0, 10);
}
function startOfMonth(): string {
  const d = new Date();
  d.setUTCDate(1);
  return d.toISOString().slice(0, 10);
}
function startOfQuarter(): string {
  const d = new Date();
  const q = Math.floor(d.getUTCMonth() / 3);
  d.setUTCMonth(q * 3, 1);
  return d.toISOString().slice(0, 10);
}
function startOfYear(): string {
  const d = new Date();
  d.setUTCMonth(0, 1);
  return d.toISOString().slice(0, 10);
}
function startOfWeek(): string {
  const d = new Date();
  const day = d.getUTCDay();
  const diff = d.getUTCDate() - day + (day === 0 ? -6 : 1);
  d.setUTCDate(diff);
  return d.toISOString().slice(0, 10);
}
function minus12Months(): string {
  const d = new Date();
  d.setUTCMonth(d.getUTCMonth() - 12);
  return d.toISOString().slice(0, 10);
}

// validateSearch — apply defaults перед zod parse, чтобы entry на /finance/dashboard
// без query params не падал (date_from/date_to required в schema для API call).
export const Route = createFileRoute('/_auth/finance/dashboard')({
  // search-catch-ok: схема общая (`@staiter/shared`), стойкость даёт `safeParse` ниже.
  validateSearch: (s: Record<string, unknown>) => {
    const withDefaults = {
      ...s,
      date_from: s.date_from ?? minus12Months(),
      date_to: s.date_to ?? today(),
    };
    /**
     * WTU-FND-L (класс) — непонятый параметр адреса НЕ валит экран.
     *
     * `DashboardQuerySchema` объявлена `.strict()`, то есть до правки любой
     * лишний параметр в ссылке (`?utm_source=…` из письма, хвост от чужой
     * закладки) ронял всю страницу в полноэкранное «Что-то пошло не так» —
     * клиентским исключением, без запроса на бэкенд и без trace. Плюс любое
     * непонятое значение валюты или даты.
     *
     * Откат идёт к ПЕРИОДУ по умолчанию целиком, а не по полю: у периода поля
     * связаны проверкой `date_to >= date_from`, и чинить их поодиночке значит
     * собирать заведомо противоречивые пары.
     */
    const parsed = DashboardQuerySchema.safeParse(withDefaults);
    if (parsed.success) return parsed.data;
    return DashboardQuerySchema.parse({ date_from: minus12Months(), date_to: today() });
  },
  component: FinanceDashboardPage,
});

type Preset = 'today' | 'wtd' | 'mtd' | 'qtd' | 'ytd' | 'last_12m' | 'custom';

const PRESET_LABELS: Record<Preset, () => string> = {
  today: m.filter_preset_today,
  wtd: m.filter_preset_wtd,
  mtd: m.filter_preset_mtd,
  qtd: m.filter_preset_qtd,
  ytd: m.filter_preset_ytd,
  last_12m: m.filter_preset_last_12m,
  custom: m.filter_preset_custom,
};

function applyPreset(p: Preset): { date_from: string; date_to: string } {
  const t = today();
  switch (p) {
    case 'today':
      return { date_from: t, date_to: t };
    case 'wtd':
      return { date_from: startOfWeek(), date_to: t };
    case 'mtd':
      return { date_from: startOfMonth(), date_to: t };
    case 'qtd':
      return { date_from: startOfQuarter(), date_to: t };
    case 'ytd':
      return { date_from: startOfYear(), date_to: t };
    case 'last_12m':
      return { date_from: minus12Months(), date_to: t };
    case 'custom':
      return { date_from: startOfMonth(), date_to: t };
  }
}

// ─── KPI Card ─────────────────────────────────────────────────────────

function deltaTone(
  deltaPct: string | null | undefined,
  semantic: 'good_up' | 'good_down',
): 'positive' | 'negative' | 'neutral' {
  if (deltaPct == null) return 'neutral';
  const n = Number(deltaPct);
  if (n === 0) return 'neutral';
  const isUp = n > 0;
  return (semantic === 'good_up' ? isUp : !isUp) ? 'positive' : 'negative';
}

function KpiCard(props: {
  title: string;
  value: MoneyAmount | { amount: string; currency: string };
  deltaPct?: string | null;
  semanticTrend?: 'good_up' | 'good_down';
  onClick?: () => void;
  badge?: string | null;
  marginPct?: string | null;
}): JSX.Element {
  const tone = deltaTone(props.deltaPct, props.semanticTrend ?? 'good_up');
  const money: MoneyAmount = {
    amount: props.value.amount,
    currency: props.value.currency as 'KZT' | 'USD' | 'EUR' | 'RUB' | 'CNY',
  };
  const Icon = tone === 'positive' ? ArrowUpRight : tone === 'negative' ? ArrowDownRight : Minus;
  const onClick = props.onClick;
  return (
    <button
      type="button"
      className={styles.kpiCard}
      onClick={onClick}
      disabled={!onClick}
      aria-label={m.hero_metric_aria({ title: props.title, value: formatMoney(money) })}
    >
      <div className={styles.kpiHead}>
        <span className={styles.kpiTitle}>{props.title}</span>
        {props.badge && <span className={styles.kpiBadge}>{props.badge}</span>}
      </div>
      {/* FE-295 Pass 63: compact формат для KPI tiles (313 500 000 → 313,5 млн ₸).
          Полное значение остаётся в aria-label выше для screen readers. */}
      <div className={styles.kpiValue}>{formatMoneyCompact(money)}</div>
      <div className={styles.kpiFoot}>
        {props.deltaPct != null && (
          <span className={[styles.deltaChip, styles[`tone_${tone}`]].join(' ')}>
            <Icon size={12} aria-hidden="true" />
            <span>
              {Number(props.deltaPct) > 0 ? '+' : ''}
              {props.deltaPct}%
            </span>
          </span>
        )}
        {props.marginPct != null && (
          <span className={styles.marginPct}>
            {props.marginPct}% {m.kpi_margin_pct_suffix()}
          </span>
        )}
      </div>
    </button>
  );
}

// ─── Charts (inline SVG, no Recharts) ─────────────────────────────────

// Compact label для оси Y: 1500000 → "1,5 M", 5000 → "5 K". Decimal-safe для display.
function formatCompactNumber(n: number): string {
  const abs = Math.abs(n);
  const sign = n < 0 ? MINUS_SIGN : '';
  if (abs >= 1e9) return `${sign}${(abs / 1e9).toFixed(1).replace('.', ',')} млрд`;
  if (abs >= 1e6) return `${sign}${(abs / 1e6).toFixed(1).replace('.', ',')} M`;
  if (abs >= 1e3) return `${sign}${(abs / 1e3).toFixed(0)} K`;
  return `${sign}${abs.toFixed(0)}`;
}

const RU_MONTH_SHORT = [
  'янв',
  'фев',
  'мар',
  'апр',
  'май',
  'июн',
  'июл',
  'авг',
  'сен',
  'окт',
  'ноя',
  'дек',
];

function formatMonthLabel(date: string): string {
  const [y, m] = date.split('-');
  const mi = Number(m) - 1;
  if (mi < 0 || mi >= 12) return date;
  return `${RU_MONTH_SHORT[mi]} ${(y ?? '').slice(2)}`;
}

/**
 * Cash Flow chart — STAITER calm UX по UI/UX guide.
 *
 * Парадигма: net flow per month (один bar на месяц, zero-axis посередине)
 * + cumulative balance area overlay. Inflow/outflow detail в detached tooltip-slot
 * справа от title (не плавающий tooltip — не клипится у краёв).
 *
 * Click на bar → drill в /payments?date_from=...&date_to=...
 *
 * Dynamic viewBox через ResizeObserver — SVG растягивается full-width контейнера,
 * geometry computed в реальных px. Высота фиксированная 360px через CSS.
 */

/** ResizeObserver hook — возвращает текущую width элемента. SSR-safe (default 800). */
function useElementWidth<T extends HTMLElement>(): [React.RefObject<T | null>, number] {
  const ref = useRef<T | null>(null);
  const [width, setWidth] = useState<number>(800);
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    setWidth(el.clientWidth);
    if (typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver((entries) => {
      const entry = entries[0];
      if (entry) setWidth(Math.max(360, entry.contentRect.width));
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  return [ref, width];
}

interface CashFlowHoverState {
  monthLabel: string;
  inflow: number;
  outflow: number;
  net: number;
  balance: number;
}

function MonthlyCashFlowChart({
  buckets,
  currency,
  onMonthClick,
  onHoverChange,
}: {
  buckets: DashboardSeriesBucket[];
  currency: DashboardCurrency;
  onMonthClick?: (bucket: DashboardSeriesBucket) => void;
  onHoverChange?: (state: CashFlowHoverState | null) => void;
}): JSX.Element {
  const [hoverIdx, setHoverIdx] = useState<number | null>(null);
  const [hostRef, hostWidth] = useElementWidth<HTMLDivElement>();

  if (buckets.length === 0) {
    return (
      <div ref={hostRef} className={styles.chartHost}>
        <EmptyState title={m.chart_empty_state()} />
      </div>
    );
  }

  // Dynamic viewBox — SVG geometry в реальных px контейнера.
  const W = Math.max(hostWidth, 360);
  const H = 360;
  const padL = 64;
  const padR = 64;
  const padT = 20;
  const padB = 40;
  const innerW = W - padL - padR;
  const innerH = H - padT - padB;

  const inflows = buckets.map((b) => Number(b.values.cash_inflow?.amount ?? 0));
  const outflows = buckets.map((b) => Number(b.values.cash_outflow?.amount ?? 0));
  const nets = inflows.map((v, i) => v - outflows[i]!);
  // Balance per month = cumulative net (running total). cash_balance из BE — это
  // cumulative от onboarding; здесь берём from-period running начиная с buckets[0].
  const balances = buckets.map((b) => Number(b.values.cash_balance?.amount ?? 0));

  // Net-flow shared scale: zero centered.
  const maxNetAbs = Math.max(Math.max(...nets.map(Math.abs)), 1);
  // Balance scale (independent — twin Y axis right side).
  const minBal = Math.min(...balances, 0);
  const maxBal = Math.max(...balances, 1);
  const balRange = Math.max(maxBal - minBal, 1);

  // Layout: zero line на 60% высоты (даём больше места для positive). Если
  // нет negative net — занижаем zero-line к низу.
  const hasNeg = nets.some((v) => v < 0);
  const zeroRatio = hasNeg ? 0.6 : 0.92;
  const zeroY = padT + innerH * zeroRatio;
  const positiveH = innerH * zeroRatio;
  const negativeH = innerH * (1 - zeroRatio);

  const groupSpan = innerW / buckets.length;
  const barW = Math.min(groupSpan * 0.55, 36);

  // Balance area path (full innerH — fills behind bars).
  const balYAt = (b: number) => padT + innerH - ((b - minBal) / balRange) * innerH;
  const balPoints = balances.map((b, i) => ({
    x: padL + i * groupSpan + groupSpan / 2,
    y: balYAt(b),
  }));
  const balLinePath =
    balPoints.length > 1
      ? `M ${balPoints[0]!.x} ${balPoints[0]!.y} ${balPoints
          .slice(1)
          .map((p, i) => {
            const prev = balPoints[i]!;
            const cx = (prev.x + p.x) / 2;
            return `Q ${cx} ${prev.y} ${p.x} ${p.y}`;
          })
          .join(' ')}`
      : '';
  const balAreaPath =
    balPoints.length > 1
      ? `${balLinePath} L ${balPoints[balPoints.length - 1]!.x} ${padT + innerH} L ${balPoints[0]!.x} ${padT + innerH} Z`
      : '';

  // Y-axis ticks (left = net flow, right = balance).
  const netTicks = [-1, -0.5, 0, 0.5, 1].filter((t) => (t >= 0 ? true : hasNeg));
  const balTicks = [0, 0.25, 0.5, 0.75, 1];

  const xLabelStep = Math.max(1, Math.ceil(buckets.length / 7));

  // Crosshair info
  const hoveredX = hoverIdx != null ? padL + hoverIdx * groupSpan + groupSpan / 2 : null;

  // Y-formatter for tooltip — full money с separator.
  const fmtTip = (n: number) => formatMoney({ amount: n.toFixed(2), currency }) ?? '—';

  // Notify parent об изменении hover state — для detached tooltip slot в header chart card.
  const setHover = (idx: number | null) => {
    setHoverIdx(idx);
    if (!onHoverChange) return;
    if (idx == null) {
      onHoverChange(null);
      return;
    }
    const b = buckets[idx]!;
    onHoverChange({
      monthLabel: formatMonthLabel(b.bucket_start),
      inflow: inflows[idx]!,
      outflow: outflows[idx]!,
      net: nets[idx]!,
      balance: balances[idx]!,
    });
  };
  const handleLeave = () => setHover(null);

  return (
    <div ref={hostRef} className={styles.chartHost}>
      <svg
        viewBox={`0 0 ${W} ${H}`}
        className={styles.chartSvg}
        role="img"
        aria-labelledby="cf-title cf-desc"
        preserveAspectRatio="xMidYMid meet"
        onMouseLeave={handleLeave}
      >
        <title id="cf-title">{m.chart_cash_flow_title()}</title>
        <desc id="cf-desc">
          {`${buckets.length} мес. Net inflow: ${fmtTip(nets.reduce((a, b) => a + b, 0))}.`}
        </desc>

        <defs>
          <linearGradient id="cf-balance-gradient" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="var(--chart-primary)" stopOpacity="0.28" />
            <stop offset="100%" stopColor="var(--chart-primary)" stopOpacity="0.04" />
          </linearGradient>
        </defs>

        {/* Balance area — primary signal, рендерится первым (под bars) */}
        {balAreaPath && (
          <>
            <path d={balAreaPath} fill="url(#cf-balance-gradient)" />
            <path
              d={balLinePath}
              fill="none"
              stroke="var(--chart-primary)"
              strokeWidth={1.5}
              strokeLinecap="round"
              strokeLinejoin="round"
              opacity={0.85}
            />
          </>
        )}

        {/* Horizontal grid: net-flow scale (left) */}
        {netTicks.map((t) => {
          const y = zeroY - t * (t >= 0 ? positiveH : negativeH);
          const value = t * maxNetAbs;
          return (
            <g key={`grid-${t}`}>
              {t !== 0 && (
                <line
                  x1={padL}
                  y1={y}
                  x2={padL + innerW}
                  y2={y}
                  stroke="var(--color-border)"
                  strokeWidth={1}
                  strokeDasharray="2 5"
                  opacity={0.45}
                />
              )}
              <text
                x={padL - 10}
                y={y + 3}
                fontSize="12"
                fill="var(--color-text-secondary)"
                textAnchor="end"
                className={styles.svgTabular}
              >
                {value === 0 ? '0' : formatCompactNumber(value)}
              </text>
            </g>
          );
        })}

        {/* Right Y-axis: balance scale */}
        {balTicks.map((t) => {
          const y = padT + innerH - t * innerH;
          const value = minBal + t * balRange;
          if (value <= 0 && t === 0) return null; // skip zero overlap with left axis
          return (
            <text
              key={`bal-tick-${t}`}
              x={padL + innerW + 10}
              y={y + 3}
              fontSize="12"
              fill="var(--accent-strong)"
              textAnchor="start"
              className={styles.svgTabularSubtle}
            >
              {formatCompactNumber(value)}
            </text>
          );
        })}

        {/* Zero baseline */}
        <line
          x1={padL}
          y1={zeroY}
          x2={padL + innerW}
          y2={zeroY}
          stroke="var(--color-border-strong, var(--color-border))"
          strokeWidth={1}
        />

        {/* Net-flow bars (one per month, centered on zero) */}
        {buckets.map((b, i) => {
          const cx = padL + i * groupSpan + groupSpan / 2;
          const net = nets[i]!;
          const ratio = net / maxNetAbs;
          const h = Math.abs(ratio) * (net >= 0 ? positiveH : negativeH);
          const y = net >= 0 ? zeroY - h : zeroY;
          const fill =
            net >= 0
              ? 'color-mix(in oklch, var(--ok), transparent 35%)'
              : 'color-mix(in oklch, var(--danger), transparent 35%)';
          const stroke = net >= 0 ? 'var(--ok)' : 'var(--danger)';
          const isHovered = hoverIdx === i;
          return (
            <g key={b.bucket_start}>
              <rect
                x={cx - barW / 2}
                y={y}
                width={barW}
                height={Math.max(h, 1.5)}
                rx={3}
                fill={fill}
                stroke={stroke}
                strokeWidth={isHovered ? 1.5 : 0.75}
                // staiter-inline-style-ok: computed cursor based on click handler.
                style={{ cursor: onMonthClick ? 'pointer' : 'default' }}
                onMouseEnter={() => setHover(i)}
                onClick={() => onMonthClick?.(b)}
              />
              {/* X-label, thinned */}
              {i % xLabelStep === 0 && (
                <text
                  x={cx}
                  y={padT + innerH + 22}
                  fontSize="12"
                  fill="var(--color-text-secondary)"
                  textAnchor="middle"
                  className={styles.svgTabular}
                >
                  {formatMonthLabel(b.bucket_start)}
                </text>
              )}
            </g>
          );
        })}

        {/* Wide invisible hit-zones — для удобного hover между bars */}
        {buckets.map((b, i) => (
          <rect
            key={`hit-${b.bucket_start}`}
            x={padL + i * groupSpan}
            y={padT}
            width={groupSpan}
            height={innerH}
            fill="transparent"
            onMouseEnter={() => setHoverIdx(i)}
            onClick={() => onMonthClick?.(b)}
            // staiter-inline-style-ok: computed cursor based on click handler.
            style={{ cursor: onMonthClick ? 'pointer' : 'default' }}
          />
        ))}

        {/* Crosshair — vertical line через всю chart-height на hovered month */}
        {hoveredX != null && (
          <>
            <line
              x1={hoveredX}
              y1={padT}
              x2={hoveredX}
              y2={padT + innerH}
              stroke="var(--color-text-secondary)"
              strokeWidth={1}
              strokeDasharray="3 3"
              opacity={0.55}
              pointerEvents="none"
            />
            {/* Balance marker — only on hovered point */}
            {balPoints[hoverIdx!] && (
              <circle
                cx={balPoints[hoverIdx!]!.x}
                cy={balPoints[hoverIdx!]!.y}
                r={4}
                fill="var(--bg-elevated, var(--color-surface-elevated))"
                stroke="var(--chart-primary)"
                strokeWidth={2}
                pointerEvents="none"
              />
            )}
          </>
        )}
      </svg>
    </div>
  );
}

/**
 * Detached tooltip slot — рендерится в header chart card справа от title.
 * Idle state: hint-текст. Hover state: 4 metrics. Не клипится у краёв chart.
 */
function CashFlowTooltipSlot({
  hover,
  currency,
}: {
  hover: CashFlowHoverState | null;
  currency: DashboardCurrency;
}): JSX.Element {
  const fmt = (n: number) => formatMoney({ amount: n.toFixed(2), currency }) ?? '—';
  if (!hover) {
    return (
      <div className={`${styles.cfTooltipSlot} ${styles.cfTooltipSlotIdle}`} aria-live="polite">
        {m.chart_cash_flow_hint()}
      </div>
    );
  }
  return (
    <div className={styles.cfTooltipSlot} aria-live="polite">
      <div className={styles.cfTooltipHead}>{hover.monthLabel}</div>
      <dl className={styles.cfTooltipRows}>
        <dt>{m.chart_legend_cash_inflow()}</dt>
        <dd className={styles.cfTooltipPos}>+{fmt(hover.inflow)}</dd>
        <dt>{m.chart_legend_cash_outflow()}</dt>
        <dd className={styles.cfTooltipNeg}>−{fmt(hover.outflow)}</dd>
        <dt className={styles.cfTooltipNetLabel}>Net</dt>
        <dd className={hover.net >= 0 ? styles.cfTooltipNetPos : styles.cfTooltipNetNeg}>
          {hover.net >= 0 ? '+' : ''}
          {fmt(hover.net)}
        </dd>
        <dt>{m.chart_legend_cash_balance()}</dt>
        <dd className={styles.cfTooltipBalance}>{fmt(hover.balance)}</dd>
      </dl>
    </div>
  );
}

function ExpenseDonut({ kpi }: { kpi: FinanceDashboardKpi }): JSX.Element {
  const total =
    Number(kpi.ar_aging.bucket_0_30.amount) +
    Number(kpi.ar_aging.bucket_31_60.amount) +
    Number(kpi.ar_aging.bucket_61_90.amount) +
    Number(kpi.ar_aging.bucket_90_plus.amount);
  if (total === 0) {
    return <EmptyState title={m.chart_empty_state()} />;
  }
  const segs = [
    {
      key: '0_30',
      value: Number(kpi.ar_aging.bucket_0_30.amount),
      color: 'var(--color-success)',
      label: m.aging_bucket_0_30(),
    },
    {
      key: '31_60',
      value: Number(kpi.ar_aging.bucket_31_60.amount),
      // S-FIX-2-FE.5.7: canonical chart palette — warning bucket.
      color: 'var(--chart-warning)',
      label: m.aging_bucket_31_60(),
    },
    {
      key: '61_90',
      value: Number(kpi.ar_aging.bucket_61_90.amount),
      // S-FIX-2-FE.5.7: 61-90 — accent для escalation visibility (gold ↔ orange).
      color: 'var(--chart-accent)',
      label: m.aging_bucket_61_90(),
    },
    {
      key: '90_plus',
      value: Number(kpi.ar_aging.bucket_90_plus.amount),
      color: 'var(--color-danger)',
      label: m.aging_bucket_90_plus(),
    },
  ];
  const W = 280;
  const H = 280;
  const cx = W / 2;
  const cy = H / 2;
  const r = 100;
  let cumulative = 0;
  const navigate = useNavigate();
  return (
    <svg
      viewBox={`0 0 ${W} ${H}`}
      className={styles.chartSvg}
      role="img"
      aria-labelledby="aging-title"
    >
      <title id="aging-title">{m.chart_ar_aging_title()}</title>
      {segs.map((seg) => {
        const start = (cumulative / total) * Math.PI * 2 - Math.PI / 2;
        cumulative += seg.value;
        const end = (cumulative / total) * Math.PI * 2 - Math.PI / 2;
        const largeArc = end - start > Math.PI ? 1 : 0;
        const x1 = cx + r * Math.cos(start);
        const y1 = cy + r * Math.sin(start);
        const x2 = cx + r * Math.cos(end);
        const y2 = cy + r * Math.sin(end);
        const path = `M ${cx} ${cy} L ${x1} ${y1} A ${r} ${r} 0 ${largeArc} 1 ${x2} ${y2} Z`;
        return (
          <path
            key={seg.key}
            d={path}
            fill={seg.color}
            opacity={0.85}
            onClick={() => {
              void navigate({ to: '/invoices/ar', search: { aging_bucket: seg.key } as never });
            }}
            // staiter-inline-style-ok: SVG path needs cursor pointer on hover.
            style={{ cursor: 'pointer' }}
          >
            <title>
              {seg.label}: {seg.value.toFixed(0)}
            </title>
          </path>
        );
      })}
      <circle cx={cx} cy={cy} r={r * 0.55} fill="var(--color-surface)" />
      <text x={cx} y={cy - 6} textAnchor="middle" fontSize="14" fill="var(--color-text)">
        AR
      </text>
      <text x={cx} y={cy + 14} textAnchor="middle" fontSize="12" fill="var(--color-text-secondary)">
        {(total / 1e6).toFixed(1)}M
      </text>
    </svg>
  );
}

function TopCounterpartiesBar({
  rows,
  currency,
}: {
  rows: TopCounterpartiesResponse['rows'];
  currency: DashboardCurrency;
}): JSX.Element {
  if (rows.length === 0) {
    return <EmptyState title={m.top_counterparties_empty()} icon={<Users size={32} />} />;
  }
  const max = Math.max(...rows.map((r) => Number(r.value.amount)), 1);
  return (
    <div className={styles.topRows}>
      {rows.map((r, i) => {
        const pct = (Number(r.value.amount) / max) * 100;
        return (
          <Link
            key={r.counterparty_id}
            to="/counterparties/$id"
            params={{ id: r.counterparty_id }}
            className={styles.topRow}
          >
            <span className={styles.topRank}>{i + 1}</span>
            <span className={styles.topName}>
              {r.name}
              {r.bin_iin && <span className={styles.topBin}>· {r.bin_iin}</span>}
            </span>
            <span className={styles.topBarWrap}>
              <span
                className={styles.topBarFill}
                // staiter-inline-style-ok: dynamic % width per row.
                style={{ width: `${pct}%` }}
                aria-hidden="true"
              />
            </span>
            <span className={styles.topValue}>
              {formatMoney({
                amount: r.value.amount,
                currency: currency as 'KZT' | 'USD' | 'EUR' | 'RUB' | 'CNY',
              })}
            </span>
            <span className={styles.topShare}>{r.share_pct}%</span>
          </Link>
        );
      })}
    </div>
  );
}

// ─── Main page ────────────────────────────────────────────────────────

function FinanceDashboardPage(): JSX.Element {
  const search = Route.useSearch();
  const navigate = Route.useNavigate();
  const filters = search;

  const setFilters = (next: Partial<typeof filters>) => {
    void navigate({ search: (prev) => ({ ...prev, ...next }) as never });
  };
  const resetFilters = () => {
    const defaults = {
      date_from: minus12Months(),
      date_to: today(),
      currency: 'KZT' as const,
      compare_to_previous_period: false,
    };
    void navigate({ search: defaults as never });
  };

  const [topMetric, setTopMetric] = useState<TopCounterpartiesMetric>('revenue');
  const [cfHover, setCfHover] = useState<CashFlowHoverState | null>(null);

  const kpiQuery = useQuery({
    queryKey: ['finance', 'dashboard', 'kpi', filters],
    queryFn: () => fetchDashboardKpi(filters),
    staleTime: 60_000,
  });

  const seriesMetrics: DashboardSeriesMetric[] = ['cash_inflow', 'cash_outflow', 'cash_balance'];
  const seriesQuery = useQuery({
    queryKey: [
      'finance',
      'dashboard',
      'series',
      filters.date_from,
      filters.date_to,
      filters.currency,
    ],
    queryFn: () =>
      fetchDashboardSeries({
        date_from: filters.date_from,
        date_to: filters.date_to,
        granularity: 'month',
        metrics: seriesMetrics,
        currency: filters.currency,
      }),
    staleTime: 60_000,
  });

  const topQuery = useQuery({
    queryKey: [
      'finance',
      'dashboard',
      'top',
      filters.date_from,
      filters.date_to,
      filters.currency,
      topMetric,
    ],
    queryFn: () =>
      fetchTopCounterparties({
        date_from: filters.date_from,
        date_to: filters.date_to,
        metric: topMetric,
        limit: 10,
        direction: 'desc',
        currency: filters.currency,
      }),
    staleTime: 60_000,
    retry: false,
  });

  const kpi = kpiQuery.data;
  const buckets = seriesQuery.data?.buckets ?? [];
  const topRows = topQuery.data?.rows ?? [];

  // Adaptive grid: AR aging показываем только если есть данные. Иначе cash flow
  // занимает full-width, AR aging переезжает в placeholder banner внизу страницы.
  const hasArData = useMemo(() => {
    if (!kpi) return false;
    const sum =
      Number(kpi.kpi.ar_aging.bucket_0_30.amount) +
      Number(kpi.kpi.ar_aging.bucket_31_60.amount) +
      Number(kpi.kpi.ar_aging.bucket_61_90.amount) +
      Number(kpi.kpi.ar_aging.bucket_90_plus.amount);
    return sum > 0;
  }, [kpi]);

  const presetActive: Preset = useMemo(() => {
    const t = today();
    if (filters.date_to !== t) return 'custom';
    if (filters.date_from === t) return 'today';
    if (filters.date_from === startOfWeek()) return 'wtd';
    if (filters.date_from === startOfMonth()) return 'mtd';
    if (filters.date_from === startOfQuarter()) return 'qtd';
    if (filters.date_from === startOfYear()) return 'ytd';
    if (filters.date_from === minus12Months()) return 'last_12m';
    return 'custom';
  }, [filters.date_from, filters.date_to]);

  // Drill-down handlers (FE.7).
  const drillTo = (path: string) => {
    void navigate({
      to: path,
      search: {
        date_from: filters.date_from,
        date_to: filters.date_to,
      } as never,
    });
  };

  // Share-link (FE.8)
  const handleShareLink = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
      // toast — отложен; используем простой alert для MVP
      // eslint-disable-next-line no-alert
      alert(m.dashboard_link_copied());
    } catch {
      // eslint-disable-next-line no-alert
      window.prompt(m.dashboard_link_copied(), window.location.href);
    }
  };

  return (
    <div className={styles.page}>
      <PageHeader
        title={m.finance_dashboard_title()}
        actions={
          <div className={styles.headerActions}>
            <Button
              variant="secondary"
              leadingIcon={<LinkIcon size={16} />}
              onClick={handleShareLink}
            >
              {m.dashboard_copy_link()}
            </Button>
            <Button variant="secondary" leadingIcon={<Download size={16} />} disabled>
              {m.dashboard_export_excel()}
            </Button>
          </div>
        }
      />

      {/* FilterBar (FE.3) */}
      <div className={styles.filterBar}>
        <div className={styles.presetGroup} role="tablist" aria-label="Date range presets">
          {(Object.keys(PRESET_LABELS) as Preset[]).map((p) => (
            <button
              key={p}
              type="button"
              role="tab"
              aria-pressed={presetActive === p}
              className={presetActive === p ? styles.presetActive : styles.preset}
              onClick={() => {
                if (p === 'custom') return;
                setFilters(applyPreset(p));
              }}
            >
              {PRESET_LABELS[p]()}
            </button>
          ))}
        </div>
        {/* S-FIX-6 fix: native date input запрещён → DateRangePicker. */}
        <DateRangePicker
          label={m.finance_dashboard_filter_period()}
          value={{ from: filters.date_from || null, to: filters.date_to || null }}
          onChange={(r) =>
            setFilters({
              date_from: r.from ?? '',
              date_to: r.to ?? '',
            })
          }
        />
        <Checkbox
          checked={filters.compare_to_previous_period}
          onChange={(e) => setFilters({ compare_to_previous_period: e.target.checked })}
          label={m.filter_compare_to_previous()}
        />
        <Button variant="ghost" onClick={resetFilters}>
          {m.filter_reset()}
        </Button>
      </div>

      {/* FE-P3-7 (QA 2026-06-04 pass3): Hero-level ProvenanceChip — computed view,
          single chip вместо per-row noise (см. reference_provenancechip_design_principle). */}
      {kpi && (
        <div className={styles.provenanceRow}>
          <ProvenanceChip source="staiter" fetched_at={new Date().toISOString()} />
        </div>
      )}

      {/* KPI grid (FE.4) — FE-NEW-2 + FE-P3-8 (QA pass3): STAITER_UI.md §4.3 max 3 hero.
          Primary 3 hero (Revenue / Net Profit / Operating Cash Flow) на base level;
          secondary 4 KPI перенесены ниже charts в BentoCard «Дополнительные показатели»
          для строгой Progressive Disclosure (Level 0 → 3 hero + charts → Level 1 Bento). */}
      {kpi ? (
        <div className={styles.kpiGrid}>
          <KpiCard
            title={m.kpi_revenue()}
            value={kpi.kpi.revenue}
            deltaPct={kpi.kpi.revenue_delta_pct}
            semanticTrend="good_up"
            onClick={() => drillTo('/finance/pnl')}
          />
          <KpiCard
            title={m.kpi_net_profit()}
            value={kpi.kpi.net_profit}
            marginPct={kpi.kpi.net_margin_pct}
            semanticTrend="good_up"
            onClick={() => drillTo('/finance/pnl')}
          />
          <KpiCard
            title={m.kpi_operating_cash_flow()}
            value={kpi.kpi.operating_cash_flow}
            deltaPct={kpi.kpi.operating_cash_flow_delta_pct}
            semanticTrend="good_up"
            onClick={() => drillTo('/finance/cash-flow')}
          />
        </div>
      ) : (
        <div className={styles.kpiGrid}>
          {Array.from({ length: 3 }).map((_, i) => (
            // eslint-disable-next-line react/no-array-index-key
            <div key={i} className={styles.kpiSkeleton} aria-hidden="true" />
          ))}
        </div>
      )}

      {/* P&L basis indicator */}
      {kpi && (
        <div className={styles.basisIndicator}>
          <span>{m.pnl_basis_label()}: </span>
          <strong>{kpi.pnl_basis === 'cash' ? m.pnl_basis_cash() : m.pnl_basis_accrual()}</strong>
          {kpi.pnl_basis === 'cash' && (
            <span className={styles.basisHint}>· {m.pnl_basis_cash_hint()}</span>
          )}
        </div>
      )}

      {/* Charts grid (FE.5) — adaptive: AR-aging пуст → cash flow full-width */}
      <div className={hasArData ? styles.chartsGrid : styles.chartsGridFull}>
        <div className={styles.chartCard}>
          <div className={styles.chartCardHeader}>
            <div className={styles.chartTitleBlock}>
              <h3 className={styles.chartTitle}>{m.chart_cash_flow_title()}</h3>
            </div>
            <CashFlowTooltipSlot hover={cfHover} currency={filters.currency} />
          </div>
          <MonthlyCashFlowChart
            buckets={buckets}
            currency={filters.currency}
            onHoverChange={setCfHover}
            onMonthClick={(b) => {
              void navigate({
                to: '/payments',
                search: {
                  date_from: b.bucket_start,
                  date_to: b.bucket_end,
                } as never,
              });
            }}
          />
          <div className={styles.chartLegend}>
            <span className={styles.legendItem}>
              <span className={styles.legendSwatchPosNet} aria-hidden="true" />
              {m.chart_legend_net_positive()}
            </span>
            <span className={styles.legendItem}>
              <span className={styles.legendSwatchNegNet} aria-hidden="true" />
              {m.chart_legend_net_negative()}
            </span>
            <span className={styles.legendItem}>
              <span className={styles.legendSwatchAccent} aria-hidden="true" />
              {m.chart_legend_cash_balance()}
            </span>
          </div>
        </div>
        {hasArData && (
          <div className={styles.chartCard}>
            <h3 className={styles.chartTitle}>{m.chart_ar_aging_title()}</h3>
            {kpi && <ExpenseDonut kpi={kpi.kpi} />}
          </div>
        )}
      </div>

      {/* Top counterparties (FE.6) */}
      <div className={styles.topSection}>
        <div className={styles.topHeader}>
          <h3 className={styles.chartTitle}>{m.top_counterparties_title()}</h3>
          <div className={styles.metricSwitcher} role="tablist">
            {(['revenue', 'ar_balance', 'ap_balance'] as const).map((t) => (
              <button
                key={t}
                type="button"
                role="tab"
                aria-pressed={topMetric === t}
                className={topMetric === t ? styles.presetActive : styles.preset}
                onClick={() => setTopMetric(t)}
              >
                {t === 'revenue'
                  ? m.top_counterparties_metric_revenue()
                  : t === 'ar_balance'
                    ? m.top_counterparties_metric_ar_balance()
                    : m.top_counterparties_metric_ap_balance()}
              </button>
            ))}
          </div>
        </div>
        <TopCounterpartiesBar rows={topRows} currency={filters.currency} />
        {topQuery.data && (
          <div className={styles.coverageHint}>
            {m.top_counterparties_coverage({ percent: `${topQuery.data.totals.coverage_pct}%` })}
          </div>
        )}
      </div>

      {/* AR aging placeholder — узкая полоса вместо пустого 1/3 контейнера */}
      {kpi && !hasArData && (
        <div className={styles.arPlaceholder}>
          <span className={styles.arPlaceholderIcon} aria-hidden="true">
            <Inbox size={16} />
          </span>
          <span>
            <span className={styles.arPlaceholderTitle}>{m.chart_ar_aging_title()}.</span>{' '}
            {m.chart_ar_aging_empty_hint()}
          </span>
        </div>
      )}

      {/* FE-P3-8 (QA pass3): secondary KPI 4 cards перенесены ниже charts в BentoCard
          для строгой Progressive Disclosure (Level 0 = 3 hero, Level 1 = breakdown). */}
      {kpi && (
        <BentoCard title={m.dashboard_secondary_kpi_title()}>
          <div className={styles.kpiGridSecondary}>
            <KpiCard
              title={m.kpi_gross_profit()}
              value={kpi.kpi.gross_profit}
              marginPct={kpi.kpi.gross_margin_pct}
              semanticTrend="good_up"
              onClick={() => drillTo('/finance/pnl')}
            />
            <KpiCard
              title={m.kpi_cash_position()}
              value={kpi.kpi.cash_position}
              badge={kpi.cash_position_is_approximation ? '≈' : null}
              semanticTrend="good_up"
            />
            <KpiCard
              title={m.kpi_ar_outstanding()}
              value={kpi.kpi.ar_outstanding}
              semanticTrend="good_down"
              onClick={() => drillTo('/invoices/ar')}
            />
            <KpiCard
              title={m.kpi_ap_outstanding()}
              value={kpi.kpi.ap_outstanding}
              semanticTrend="good_down"
              onClick={() => drillTo('/invoices/ap')}
            />
          </div>
        </BentoCard>
      )}
    </div>
  );
}
